import { useAxiosConfig } from '@shared/api/AxiosConfigProvider';
import { refreshQueries } from '@shared/api/hooks/refresh-queries';
import {
  getCompanyReportsActivitiesFoodListQueryKey,
  getCompanyReportsActivitiesSupplyChainLogisticsListQueryKey,
  getCompanyReportsRetrieveQueryKey,
  useCompanyReportsActivitiesSupplyChainLogisticsCreate,
  useCompanyReportsActivitiesSupplyChainLogisticsList,
  useCompanyReportsActivitiesSupplyChainLogisticsPreviewRetrieve,
  useCompanyReportsSupplyChainLogisticsFlowCreate,
  useCompanyReportsSupplyChainLogisticsFlowDestroy,
  useCompanyReportsSupplyChainLogisticsFlowRetrieve,
  useCompanyReportsSupplyChainLogisticsFlowTemplateCreate,
  useCompanyReportsSupplyChainLogisticsFlowUpdate,
} from '@shared/api/lib/company-reports/company-reports';
import {
  CompanyReportsActivitiesSupplyChainLogisticsListParams,
  LogisticsFlowCreate,
  LogisticsFlowUpdate,
  PaginatedSupplyChainLogisticsActivitiesListList,
  SupplyChainLogisticsActivityCreate,
} from '@shared/api/types';
import { useQueryClient } from '@tanstack/react-query';

export const useCreateSupplyChainFlow = () => {
  return useCompanyReportsSupplyChainLogisticsFlowCreate({
    request: useAxiosConfig(),
  });
};

export const useCreateSupplyChainActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesSupplyChainLogisticsCreate<SupplyChainLogisticsActivityCreate>(
    {
      request: useAxiosConfig(),
      mutation: {
        onSuccess: (data, variables) => {
          void refreshQueries(
            queryClient,
            getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
          );
          return refreshQueries(
            queryClient,
            getCompanyReportsActivitiesSupplyChainLogisticsListQueryKey(
              variables.companyReportUuid
            )
          );
        },
      },
    }
  );
};

export const useGenerateSupplyChainFlowTemplate = () => {
  return useCompanyReportsSupplyChainLogisticsFlowTemplateCreate<LogisticsFlowCreate>(
    {
      request: useAxiosConfig(),
    }
  );
};

export const useGetSupplyChainActivityList = (
  companyReportUuid: string,
  params?: CompanyReportsActivitiesSupplyChainLogisticsListParams
) => {
  return useCompanyReportsActivitiesSupplyChainLogisticsList<PaginatedSupplyChainLogisticsActivitiesListList>(
    companyReportUuid,
    params,
    {
      request: useAxiosConfig(),
    }
  );
};

export const useGetSupplyChainFlow = (flowUuid: string) => {
  return useCompanyReportsSupplyChainLogisticsFlowRetrieve(flowUuid, {
    request: useAxiosConfig(),
  });
};

export const useUpdateSupplyChainFlow = ({
  companyReportUuid,
}: {
  companyReportUuid: string;
}) => {
  const queryClient = useQueryClient();
  return useCompanyReportsSupplyChainLogisticsFlowUpdate<LogisticsFlowUpdate>({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: () => {
        void refreshQueries(
          queryClient,
          getCompanyReportsRetrieveQueryKey(companyReportUuid)
        );
        return Promise.all([
          refreshQueries(
            queryClient,
            getCompanyReportsActivitiesSupplyChainLogisticsListQueryKey(
              companyReportUuid
            )
          ),
          refreshQueries(
            queryClient,
            getCompanyReportsActivitiesFoodListQueryKey(companyReportUuid)
          ),
        ]);
      },
    },
  });
};

export const useGetSupplyChainLogisticsPreview = ({
  companyReportUuid,
  params,
  enabled = true,
}: {
  companyReportUuid: string;
  params: CompanyReportsActivitiesSupplyChainLogisticsListParams;
  enabled?: boolean;
}) => {
  return useCompanyReportsActivitiesSupplyChainLogisticsPreviewRetrieve(
    companyReportUuid,
    params,
    {
      request: useAxiosConfig(),
      query: {
        enabled,
        cacheTime: 0,
        staleTime: 0,
      },
    }
  );
};

export const useDeleteSupplyChainActivity = (companyReportUuid: string) => {
  const queryClient = useQueryClient();
  return useCompanyReportsSupplyChainLogisticsFlowDestroy({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: () => {
        void refreshQueries(
          queryClient,
          getCompanyReportsRetrieveQueryKey(companyReportUuid)
        );
        return refreshQueries(
          queryClient,
          getCompanyReportsActivitiesSupplyChainLogisticsListQueryKey(
            companyReportUuid
          )
        );
      },
    },
  });
};
